import { BehaviorSubject, combineLatest, filter, of, switchMap, takeUntil } from 'rxjs';

import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, OnInit } from '@angular/core';
import { DesktopInventoryCategoriesTableComponent } from '@inventory/components/desktop-inventory-categories-table/desktop-inventory-categories-table.component';
import { InventoryCategoryCreateOffcanvasComponent } from '@inventory/components/inventory-category-create-offcanvas/inventory-category-create-offcanvas.component';
import { InventoryCategoryDetailsOffcanvasComponent } from '@inventory/components/inventory-category-details-offcanvas/inventory-category-details-offcanvas.component';
import { MobileInventoryCategoriesTableComponent } from '@inventory/components/mobile-inventory-categories-table/mobile-inventory-categories-table.component';
import { IInventoryCategory } from '@inventory/models/inventory';
import { InventoryCategoriesService } from '@inventory/services/inventory-categories/inventory-categories.service';
import { LayoutService } from '@layout/services/layout/layout.service';
import { IOrganisationAndOrganisationUser, OrganisationUserAccessLevel } from '@organisations/models/organisation-user';
import { OrganisationSelectedService } from '@organisations/services/organisation-selected/organisation-selected.service';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { UnauthorisedBannerComponent } from '@shared/components/ui/unauthorised-banner/unauthorised-banner.component';
import { ToastTypes } from '@shared/models/toast';
import { OffcanvasService } from '@shared/services/offcanvas/offcanvas.service';
import { ToastService } from '@shared/services/toast/toast.service';

@Component({
	selector: 'app-inventory-categories',
	imports: [
		CommonModule,
		LoadingStateComponent,
		UnauthorisedBannerComponent,
		DesktopInventoryCategoriesTableComponent,
		MobileInventoryCategoriesTableComponent,
	],
	templateUrl: './inventory-categories.component.html',
	styleUrl: './inventory-categories.component.scss',
})
export class InventoryCategoriesComponent implements OnInit, OnDestroy {
	destroyed$ = new EventEmitter<void>();
	private inventoryCategoryService = inject(InventoryCategoriesService);
	private organisationSelectedService = inject(OrganisationSelectedService);
	private layoutService = inject(LayoutService);
	private offcanvasService = inject(OffcanvasService);
	private toastService = inject(ToastService);

	selectedOrganisation: IOrganisationAndOrganisationUser | null;

	isLoading = true;
	categoriesLoading = true;
	isMobileSize = true;
	userHasAccess = false;

	inventoryCategories: Array<IInventoryCategory> = [];
	showArchived$ = new BehaviorSubject<boolean>(false);

	ngOnInit(): void {
		this.layoutService.isMobileSize$.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
			this.isMobileSize = value;
		});

		combineLatest([this.organisationSelectedService.selectedOrganisation, this.showArchived$])
			.pipe(
				takeUntil(this.destroyed$),
				filter(([selectedOrganisation]) => !!selectedOrganisation),
				switchMap(([selectedOrganisation, showArchived]) => {
					this.selectedOrganisation = selectedOrganisation;
					if (
						selectedOrganisation &&
						[OrganisationUserAccessLevel.OWNER, OrganisationUserAccessLevel.ADMIN].includes(
							selectedOrganisation.organisationUser.accessLevel,
						)
					) {
						this.userHasAccess = true;
						this.categoriesLoading = true;

						return this.inventoryCategoryService.getInventoryCategoriesByOrganisation(
							selectedOrganisation.organisationUser.organisation,
							showArchived,
						);
					} else {
						this.userHasAccess = false;
						this.isLoading = false;
						return of([]);
					}
				}),
			)
			.subscribe((inventoryCategories) => {
				this.inventoryCategories = inventoryCategories;
				this.isLoading = false;
				this.categoriesLoading = false;
			});
	}

	ngOnDestroy(): void {
		this.destroyed$.emit();
	}

	onInventoryCategoryEdit(categoryItem: IInventoryCategory) {
		const ref = this.offcanvasService.open(InventoryCategoryDetailsOffcanvasComponent, 'end', true, 'oc-lg');
		const componentInstance = ref.componentInstance as InventoryCategoryDetailsOffcanvasComponent;
		componentInstance.inventoryCategory = categoryItem;
	}

	onInventoryCategoryArchive(inventoryCategory: IInventoryCategory) {
		this.inventoryCategoryService
			.archiveInventoryCategory(inventoryCategory.id as string)
			.then(() => {
				this.toastService.showToast({
					title: 'Inventory Category',
					message: `Successfully archived inventory category.`,
					type: ToastTypes.SUCCESS,
				});
			})
			.catch((error) => {
				console.error('Error archiving inventory category:', error);
				this.toastService.showToast({
					title: 'Inventory Category',
					message: `Failed to archive inventory Category: ${error.message}`,
					type: ToastTypes.ERROR,
				});
			});
	}

	onInventoryCategoryUnarchive(inventoryCategory: IInventoryCategory) {
		this.inventoryCategoryService
			.unArchiveInventoryCategory(inventoryCategory.id as string)
			.then(() => {
				this.toastService.showToast({
					title: 'Inventory Category',
					message: `Successfully unarchived inventory category.`,
					type: ToastTypes.SUCCESS,
				});
			})
			.catch((error) => {
				console.error('Error unarchiving inventory category:', error);
				this.toastService.showToast({
					title: 'Inventory Category',
					message: `Failed to unarchive inventory category: ${error.message}`,
					type: ToastTypes.ERROR,
				});
			});
	}

	openAddInventoryCategoryOffcanvas() {
		const ref = this.offcanvasService.open(InventoryCategoryCreateOffcanvasComponent, 'end', true, 'oc-lg');
		const componentInstance = ref.componentInstance as InventoryCategoryCreateOffcanvasComponent;
		componentInstance.organisationRef = this.selectedOrganisation!.organisationUser.organisation;
	}
}
